import { GetBookParams } from '@/data/services/bookServices';
import { GetChapter } from '@/data/services/chapterServices';
import { Section } from '@/models/Book';
import Chapter from '@/models/Chapter';
import { PlusIcon, TrashIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import SectionService from '../sections/SectionService';
import SubchapterFormTitle from './SubchapterFormTitle';

import useCourseEditing from '@/data/hook/useCourseEditing';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '../../../ConditionalRenderer';
import IconButton from '../../../buttons/IconButton';
import ChapterLayoutForm from './layout/ChapterLayoutForm';
import {
  DragDropItem,
  DragDropList,
} from '@/components/ui/custom/dataInput/DragDrop';
import { dropHandler } from '@/utils/dragdrop';
import { OnDragEndResponder } from '@hello-pangea/dnd';
import { DragIcon } from '@/components/icons';
import Subchapter from '@/models/Subchapter';
import { LayoutChapter } from '@/models/ChapterLayout';

export type CombinedFormFields = {
  layout: LayoutChapter;
  title: string;
};

type ChapterFormProps = {
  chapter: Chapter;
  onAddSection: () => void;
  isDeletingChapter?: boolean;
  isAddingSection?: boolean;
  onDeleteSection: (section: Section) => void;
  onUpdateSection: (section: Section) => void;
  onDeleteSubchapter: () => void;
  onReorderSections: (sections: Section[]) => void;
  onSubmitChapter: (params: {
    chapter: Partial<CombinedFormFields>;
    validated: boolean;
  }) => void;

  onSubmitSubchapter: (params: {
    subchapter: Partial<CombinedFormFields>;
    validated: boolean;
  }) => void;

  subchapter: Subchapter;
} & GetBookParams;

export default function ChapterForm({
  chapter,
  onSubmitChapter,
  onAddSection,
  onUpdateSection,
  isAddingSection,
  onDeleteSubchapter,
  isDeletingChapter,
  onDeleteSection,
  subchapter,
  onSubmitSubchapter,
  ...bookparams
}: ChapterFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'chapter.form',
  });
  const { t: tPage } = useTranslation('translation', {
    keyPrefix: 'editMaterialPage',
  });
  const { allowBigChanges } = useCourseEditing();

  const [layout, setLayout] = useState(false);

  const { control, watch } = useForm<CombinedFormFields>({
    defaultValues: {
      layout: chapter.layout,
      title: subchapter.title,
    },
  });

  useEffect(() => {
    const subscription = watch((formData, { name }) => {
      let isValid = true;

      if (name === 'layout') {
        isValid = !!formData.layout && !!formData.layout.type;

        onSubmitChapter({
          chapter: { layout: formData.layout as LayoutChapter },
          validated: isValid,
        });
      } else if (name === 'title') {
        isValid = !!formData.title && formData.title.trim().length > 0;

        onSubmitSubchapter({
          subchapter: { title: formData.title },
          validated: isValid,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, onSubmitChapter, onSubmitSubchapter]);

  return (
    <form className="flex flex-col gap-6 w-full" data-testid="chapterForm">
      <div className="flex justify-between items-center w-full flex-wrap gap-2.5">
        <SubchapterFormTitle control={control} />

        <div className="flex gap-2.5">
          <IconButton
            color="custom"
            type="button"
            tooltip={t('editLayout')}
            testId="editLayout"
            onClick={() => setLayout(prev => !prev)}
            className="text-primary w-5 h-5 font-500 gap-1.5"
            icon={<Squares2X2Icon />}
          />
          <IconButton
            color="custom"
            type="button"
            loading={isAddingSection}
            onClick={onAddSection}
            tooltip={!allowBigChanges ? tPage('cantModify') : t('addSection')}
            disabled={!allowBigChanges}
            testId="addSection"
            className="text-primary font-500  gap-1.5 w-5 h-5"
            icon={<PlusIcon />}
          />
          <IconButton
            color="custom"
            type="button"
            tooltip={
              !allowBigChanges ? tPage('cantModify') : t('deleteSubchapter')
            }
            disabled={!allowBigChanges}
            onClick={onDeleteSubchapter}
            testId="deleteSubchapter"
            loading={isDeletingChapter}
            className="text-error font-500 w-5 h-5 gap-1.5"
            icon={<TrashIcon />}
          />
        </div>
      </div>
      <ConditionalRenderer condition={layout}>
        <Controller
          name="layout"
          control={control}
          render={({ field }) => (
            <ChapterLayoutForm
              sections={subchapter.sections || []}
              layout={field.value || { type: 'layout1' }}
              onChange={field.onChange}
              onFinish={() => setLayout(false)}
            />
          )}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={!layout}>
        <SectionsService
          key={JSON.stringify(subchapter.sections?.map(section => section.id))}
          subchapterId={subchapter.id}
          sections={subchapter.sections || []}
          onDeleteSection={onDeleteSection}
          onUpdateSection={onUpdateSection}
          chapterId={chapter.id}
          {...bookparams}
        />
      </ConditionalRenderer>
    </form>
  );
}

type SectionsListProps = {
  sections: Section[];
  onDeleteSection: (section: Section) => void;
  onUpdateSection: (section: Section) => void;
  onReorderSections: (sections: Section[]) => void;
  subchapterId: number;
} & GetChapter;

const SectionsService = ({
  sections: chapterSections,
  onDeleteSection,
  onUpdateSection,
  onReorderSections,
  ...props
}: SectionsListProps) => {
  const [sections, setSections] = useState(chapterSections);
  const { allowBigChanges } = useCourseEditing();

  const handleDragEnd: OnDragEndResponder = res => {
    const reordenedResult = dropHandler(res, chapterSections);
    setSections(reordenedResult);
    onReorderSections(reordenedResult);
  };
  return (
    <DragDropList
      className="relative flex flex-col gap-2.5 w-full"
      groupId="sections"
      onDragEnd={handleDragEnd}
    >
      {sections.map((section, index) => (
        <DragDropItem
          key={section.id}
          id={'section' + section.id}
          index={index}
          disabled={!allowBigChanges}
          dragIconHandle={
            <div className="relative h-full flex items-center top-1.5">
              <DragIcon className="w-5" />
            </div>
          }
        >
          <SectionService
            section={section}
            isUnique={sections.length === 1}
            onSubmit={onUpdateSection}
            {...props}
            onDelete={() => onDeleteSection(section)}
          />
        </DragDropItem>
      ))}
    </DragDropList>
  );
};
